// Board → canvas snapshotter for exports.
//
// Approach: temporarily reset the LIVE Konva stage's transform (scale=1,
// origin offset = -bbox so the bbox aligns with the stage origin), capture
// the bbox region via stage.toCanvas(), then restore the original transform.
// The user sees a single frame of "flash to scale 1" while the export runs
// — acceptable for an explicit Export action and far simpler than mounting
// an off-screen React-konva tree (which fights React context boundaries).
//
// We grow the stage's pixel size if the bbox is larger than the current
// viewport. Konva's drawing surface clips to its own width/height, so
// without this a tall board would crop. The original width/height is
// restored at the end.

import type Konva from 'konva';
import type { Shape } from '@frame/shared';
import { shapeBBox } from '../canvas/binding/shapesMap';

export interface SnapshotOpts {
  pixelRatio?: number;
  padding?: number;
  background?: string;
}

const DEFAULT_PADDING = 32;
const DEFAULT_BACKGROUND = '#ffffff';
const DEFAULT_PIXEL_RATIO = 2;

export interface ShapeBoundingBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function unionShapeBBox(shapes: readonly Shape[]): ShapeBoundingBox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let any = false;
  for (const shape of shapes) {
    const bbox = shapeBBox(shape);
    if (!bbox) continue;
    any = true;
    if (bbox.x < minX) minX = bbox.x;
    if (bbox.y < minY) minY = bbox.y;
    if (bbox.x + bbox.w > maxX) maxX = bbox.x + bbox.w;
    if (bbox.y + bbox.h > maxY) maxY = bbox.y + bbox.h;
  }
  if (!any) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// Snapshot the live stage's bbox region to an HTMLCanvasElement. Returns
// a fresh canvas containing only the bbox + padding at pixelRatio density.
// The stage's transform is restored before the function returns.
export function snapshotStageRegion(
  stage: Konva.Stage,
  shapes: readonly Shape[],
  opts: SnapshotOpts = {},
): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const bbox = unionShapeBBox(shapes);
    if (!bbox) {
      reject(new Error('No exportable shapes on the board.'));
      return;
    }
    const padding = opts.padding ?? DEFAULT_PADDING;
    const pixelRatio = opts.pixelRatio ?? DEFAULT_PIXEL_RATIO;
    const background = opts.background ?? DEFAULT_BACKGROUND;
    const w = Math.ceil(bbox.w + padding * 2);
    const h = Math.ceil(bbox.h + padding * 2);

    // Snapshot original transform so we can restore exactly what was there.
    const original = {
      scaleX: stage.scaleX(),
      scaleY: stage.scaleY(),
      x: stage.x(),
      y: stage.y(),
      width: stage.width(),
      height: stage.height(),
    };

    try {
      // Resize the stage to fit the bbox; reset transform so world (bbox.x,
      // bbox.y) lands at screen (padding, padding).
      stage.size({ width: w, height: h });
      stage.scale({ x: 1, y: 1 });
      stage.position({ x: padding - bbox.x, y: padding - bbox.y });
      // Force a synchronous redraw so toCanvas sees the new transform.
      stage.batchDraw();

      const out = stage.toCanvas({
        x: 0,
        y: 0,
        width: w,
        height: h,
        pixelRatio,
      });

      // Composite the background onto a new canvas — Konva's toCanvas
      // returns a transparent surface (the dot-grid + bg-canvas come from
      // the container's CSS, not the stage). JPEG needs a non-transparent
      // backdrop so we draw the background ourselves.
      const composed = document.createElement('canvas');
      composed.width = out.width;
      composed.height = out.height;
      const ctx = composed.getContext('2d');
      if (!ctx) {
        throw new Error('2D context unavailable for export composite.');
      }
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, composed.width, composed.height);
      ctx.drawImage(out, 0, 0);

      resolve(composed);
    } catch (err) {
      reject(err);
    } finally {
      // Always restore — even if toCanvas threw — so the editor doesn't
      // get stuck at the export transform.
      stage.size({ width: original.width, height: original.height });
      stage.scale({ x: original.scaleX, y: original.scaleY });
      stage.position({ x: original.x, y: original.y });
      stage.batchDraw();
    }
  });
}

export function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error(`Failed to encode ${mimeType}.`));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality,
    );
  });
}
